import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { MessageSquare, NotebookPen, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useGetNotes } from "../hooks";

import { logEvent } from "@shared/analytics/analytics";
import { PageHeader } from "@shared/components/PageHeader";
import { NoteDialog } from "./notesDialog";

// Simplified props interface (kept for consistency)
export interface NotesTabProps {
  requestId: string;
  noteType?: string;
  source: string;
  title?: string;
}

export function NotesTab({ requestId, noteType, source, title }: NotesTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: notes } = useGetNotes(requestId, source);
  useEffect(() => {
    logEvent("notes_tab_viewed");
  }, []);
  return (
    <div className="space-y-6">
      <div className="flex flex-row items-center justify-between">
      <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <NotebookPen className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <PageHeader
              title="Notes"
              subtitle="Manage notes for this request"
            />
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setIsDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Note
        </Button>
      </div>
      <CardContent>
        {notes?.count === 0 ? (
          <div className="text-center p-8">
            <MessageSquare className="h-10 w-10 text-gray-400 mx-auto mb-2" />
            <h3 className="text-lg font-medium text-gray-600">No Notes</h3>
            <p className="text-sm text-gray-500">
              No notes available for this request
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {notes?.results?.map((note, index) => (
              <div
                key={note.id || `note-${index}`}
                className="p-4 border rounded-md bg-gray-50"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{note?.note}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {note.createdDate}
                    </span>
                  </div>
                </div>
                <div className="mb-2">
                  <span className="text-sm text-gray-600">
                    By:{" "}
                    {note.createdBy
                      ? note.createdBy
                      : note.createdUserRemoteName ?? "NA"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add Note Dialog */}
      <NoteDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        itemId={requestId}
        source={source}
        title={title}
      />
    </div>
  );
}
